import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import {
  ArrowUpTrayIcon,
  CameraIcon,
  PlayIcon,
  ShieldExclamationIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import {
  deleteTripPhoto,
  getClaimPhotos,
  getTripPhotos,
  uploadClaimPhotos,
  uploadTripPhotos,
} from "api/booking/booking";
import { OdometerReadResult } from "api/vehicles/vehicle";

type Phase = "pre" | "post";
type Section = "odometer" | "images" | "claim";

type TripPhoto = {
  id: number;
  image: string;
  image_type: "pre_trip" | "post_trip" | "pre_odometer" | "post_odometer";
  is_claim?: boolean;
  created_at?: string;
  added_by?: { first_name?: string; last_name?: string };
};

interface Props {
  open: boolean;
  onClose: () => void;
  bookingId: string | number | undefined;
  bookingLabel?: { vehicle?: string; plate?: string };
  preOdometerImage: string | null;
  postOdometerImage: string | null;
  preOdometerLoading: boolean;
  postOdometerLoading: boolean;
  preOdometerResult: OdometerReadResult | null;
  postOdometerResult: OdometerReadResult | null;
  preOdometerError: string | null;
  postOdometerError: string | null;
  onUploadOdometer: (file: File, isPre: boolean) => void | Promise<void>;
  initialPhase?: Phase;
  onPhotosChanged?: (counts: { pre: number; post: number }) => void;
}

const GaugeIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 14l4-4" />
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </svg>
);

const TripInspectionModal: FC<Props> = ({
  open,
  onClose,
  bookingId,
  bookingLabel,
  preOdometerImage,
  postOdometerImage,
  preOdometerLoading,
  postOdometerLoading,
  preOdometerResult,
  postOdometerResult,
  preOdometerError,
  postOdometerError,
  onUploadOdometer,
  initialPhase = "pre",
  onPhotosChanged,
}) => {
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [section, setSection] = useState<Section>("images");
  const [tripPhotos, setTripPhotos] = useState<TripPhoto[]>([]);
  const [pendingFiles, setPendingFiles] = useState<
    { file: File; url: string }[]
  >([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [claimPhotos, setClaimPhotos] = useState<TripPhoto[]>([]);
  const [claimPendingFiles, setClaimPendingFiles] = useState<
    { file: File; url: string }[]
  >([]);
  const [uploadingClaim, setUploadingClaim] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhase(initialPhase);
      setSection("images");
    }
  }, [open, initialPhase]);

  const onPhotosChangedRef = useRef(onPhotosChanged);
  onPhotosChangedRef.current = onPhotosChanged;

  const fetchPhotos = useCallback(async () => {
    if (!bookingId) return;
    const res: any = await getTripPhotos(String(bookingId));
    const list: TripPhoto[] = Array.isArray(res)
      ? res
      : Array.isArray(res?.data)
        ? res.data
        : [];
    setTripPhotos(list);
    onPhotosChangedRef.current?.({
      pre: list.filter((p) => p.image_type === "pre_trip").length,
      post: list.filter((p) => p.image_type === "post_trip").length,
    });
  }, [bookingId]);

  const fetchClaimPhotos = useCallback(async () => {
    if (!bookingId) return;
    const res: any = await getClaimPhotos(String(bookingId));
    const list: TripPhoto[] = Array.isArray(res)
      ? res
      : Array.isArray(res?.data)
        ? res.data
        : [];
    setClaimPhotos(list);
  }, [bookingId]);

  useEffect(() => {
    if (open) {
      fetchPhotos();
      fetchClaimPhotos();
    }
  }, [open, fetchPhotos, fetchClaimPhotos]);

  const onDrop = useCallback((accepted: File[]) => {
    setPendingFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  }, []);

  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  const claimPendingFilesRef = useRef(claimPendingFiles);
  claimPendingFilesRef.current = claimPendingFiles;
  useEffect(() => {
    return () => {
      [...pendingFilesRef.current, ...claimPendingFilesRef.current].forEach(
        (p) => {
          try {
            URL.revokeObjectURL(p.url);
          } catch {
            // ignore
          }
        },
      );
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/webm": [".webm"],
    },
    multiple: true,
  });

  const onDropClaim = useCallback((accepted: File[]) => {
    if (accepted.length === 0) return;
    setClaimPendingFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  }, []);

  const startClaimUpload = async () => {
    if (!bookingId || claimPendingFiles.length === 0) return;
    setUploadingClaim(true);
    try {
      // Claim evidence is a single, phase-agnostic bucket — always tag uploads
      // as pre_trip on the wire so the gallery stays unified.
      const res: any = await uploadClaimPhotos(
        claimPendingFiles.map((p) => p.file),
        String(bookingId),
        true,
      );
      if (res?.error) throw new Error(res.error);
      toast.success("Damage proof uploaded successfully");
      claimPendingFiles.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {
          // ignore
        }
      });
      setClaimPendingFiles([]);
      await fetchClaimPhotos();
    } catch {
      toast.error("Damage proof upload failed");
    } finally {
      setUploadingClaim(false);
    }
  };

  const removeClaimPending = (idx: number) =>
    setClaimPendingFiles((prev) => {
      const target = prev[idx];
      if (target) {
        try {
          URL.revokeObjectURL(target.url);
        } catch {
          // ignore
        }
      }
      return prev.filter((_, i) => i !== idx);
    });

  const clearClaimPending = () => {
    claimPendingFiles.forEach((p) => {
      try {
        URL.revokeObjectURL(p.url);
      } catch {
        // ignore
      }
    });
    setClaimPendingFiles([]);
  };

  const {
    getRootProps: getClaimRootProps,
    getInputProps: getClaimInputProps,
    isDragActive: isClaimDragActive,
  } = useDropzone({
    onDrop: onDropClaim,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/webm": [".webm"],
    },
    multiple: true,
  });

  const startUpload = async () => {
    if (!bookingId || pendingFiles.length === 0) return;
    setUploading(true);
    try {
      const res: any = await uploadTripPhotos(
        pendingFiles.map((p) => p.file),
        String(bookingId),
        phase === "pre",
      );
      if (res?.error) throw new Error(res.error);
      toast.success("Files uploaded successfully");
      pendingFiles.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {
          // ignore
        }
      });
      setPendingFiles([]);
      await fetchPhotos();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePending = (idx: number) =>
    setPendingFiles((prev) => {
      const target = prev[idx];
      if (target) {
        try {
          URL.revokeObjectURL(target.url);
        } catch {
          // ignore
        }
      }
      return prev.filter((_, i) => i !== idx);
    });

  const clearPending = () => {
    pendingFiles.forEach((p) => {
      try {
        URL.revokeObjectURL(p.url);
      } catch {
        // ignore
      }
    });
    setPendingFiles([]);
  };

  const removeUploaded = async (id: number) => {
    setDeletingId(id);
    try {
      const res: any = await deleteTripPhoto(id);
      if (res?.error) throw new Error(res.error);
      setTripPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error("Failed to remove photo");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredPhotos = useMemo(
    () =>
      tripPhotos.filter((p) =>
        phase === "pre"
          ? p.image_type === "pre_trip"
          : p.image_type === "post_trip",
      ),
    [tripPhotos, phase],
  );

  // Claim is phase-agnostic — show every uploaded claim file regardless of pre/post.
  const filteredClaimPhotos = claimPhotos;

  const odometerPhotoRecord = useMemo<TripPhoto | null>(() => {
    const targetType = phase === "pre" ? "pre_odometer" : "post_odometer";
    const matches = tripPhotos
      .filter((p) => p.image_type === targetType)
      .sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime(),
      );
    return matches[0] ?? null;
  }, [tripPhotos, phase]);

  const odometerLoading =
    phase === "pre" ? preOdometerLoading : postOdometerLoading;
  const odometerImage = phase === "pre" ? preOdometerImage : postOdometerImage;
  const odometerResult = phase === "pre" ? preOdometerResult : postOdometerResult;
  const odometerError = phase === "pre" ? preOdometerError : postOdometerError;

  return (
    <>
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-8">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.99]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.99]"
            >
              <DialogPanel className="w-full max-w-[960px] h-[80vh] max-h-[calc(100vh-64px)] bg-bg-color border border-secondary-100 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-secondary-100">
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-bold text-text-color">
                      Upload Trip Photos
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-text-color/70">
                      <span>Booking #{bookingId ?? ""}</span>
                      {bookingLabel?.vehicle && (
                        <>
                          <span className="text-text-color/40">·</span>
                          <span>{bookingLabel.vehicle}</span>
                        </>
                      )}
                      {bookingLabel?.plate && (
                        <>
                          <span className="text-text-color/40">·</span>
                          <span>{bookingLabel.plate}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-text-color"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Phase tabs — hidden in the Claim section since claim is phase-agnostic */}
                {section !== "claim" && (
                  <div className="flex bg-secondary-50 border-b border-secondary-100">
                    <PhaseTab
                      active={phase === "pre"}
                      onClick={() => setPhase("pre")}
                      label="Pre-trip"
                    />
                    <PhaseTab
                      active={phase === "post"}
                      onClick={() => setPhase("post")}
                      label="Post-trip"
                    />
                  </div>
                )}

                {/* Body */}
                <div className="flex-1 flex min-h-0 overflow-hidden flex-col sm:flex-row">
                  <aside className="sm:w-[200px] flex-shrink-0 border-b sm:border-b-0 sm:border-r border-secondary-100 p-4 flex sm:flex-col flex-row gap-1 overflow-x-auto">
                    <div className="hidden sm:block px-3.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-color/50">
                      Inspection
                    </div>
                    <SubNavItem
                      active={section === "images"}
                      onClick={() => setSection("images")}
                      icon={<CameraIcon className="w-[18px] h-[18px]" />}
                      label="Car images"
                    />
                    <SubNavItem
                      active={section === "odometer"}
                      onClick={() => setSection("odometer")}
                      icon={<GaugeIcon className="w-[18px] h-[18px]" />}
                      label="Odometer"
                    />
                    <div
                      className="hidden sm:block my-2 border-t border-secondary-100"
                      aria-hidden="true"
                    />
                    <div className="hidden sm:block px-3.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-color/50">
                      Other
                    </div>
                    <SubNavItem
                      active={section === "claim"}
                      onClick={() => setSection("claim")}
                      icon={
                        <ShieldExclamationIcon className="w-[18px] h-[18px]" />
                      }
                      label="Damage proof"
                    />
                  </aside>

                  <section className="flex-1 min-w-0 p-6 overflow-y-auto">
                    {section === "odometer" ? (
                      <OdometerPanel
                        phase={phase}
                        image={odometerImage}
                        photoRecord={odometerPhotoRecord}
                        loading={odometerLoading}
                        result={odometerResult}
                        error={odometerError}
                        onPick={(file) =>
                          onUploadOdometer(file, phase === "pre")
                        }
                        preResult={preOdometerResult}
                        postResult={postOdometerResult}
                      />
                    ) : section === "claim" ? (
                      <ClaimEvidencePanel
                        rootProps={getClaimRootProps()}
                        inputProps={getClaimInputProps()}
                        isDragActive={isClaimDragActive}
                        pendingItems={claimPendingFiles}
                        onRemovePending={removeClaimPending}
                        onClearPending={clearClaimPending}
                        onUpload={startClaimUpload}
                        uploading={uploadingClaim}
                        photos={filteredClaimPhotos}
                        onPreview={setPreviewImage}
                      />
                    ) : (
                      <CarImagesPanel
                        phase={phase}
                        rootProps={getRootProps()}
                        inputProps={getInputProps()}
                        isDragActive={isDragActive}
                        pendingItems={pendingFiles}
                        onRemovePending={removePending}
                        onClearPending={clearPending}
                        onUpload={startUpload}
                        uploading={uploading}
                        photos={filteredPhotos}
                        onRemoveUploaded={removeUploaded}
                        deletingId={deletingId}
                        onPreview={setPreviewImage}
                      />
                    )}
                  </section>
                </div>

              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>

    <Transition appear show={previewImage !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setPreviewImage(null)}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/85" aria-hidden="true" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="relative max-h-[90vh] max-w-[90vw] overflow-auto rounded-lg bg-black">
                  <button
                    type="button"
                    onClick={() => setPreviewImage(null)}
                    className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                    aria-label="Close preview"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  {previewImage && (
                    <img
                      src={previewImage}
                      alt=""
                      className="max-h-[90vh] max-w-[90vw] object-contain"
                    />
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default TripInspectionModal;

/* ---------- Tabs ---------- */

const PhaseTab: FC<{ active: boolean; onClick: () => void; label: string }> = ({
  active,
  onClick,
  label,
}) => (
  <button
    onClick={onClick}
    className={`flex-1 px-5 py-3.5 text-sm font-semibold text-center transition-all ${
      active
        ? "text-text-color bg-bg-color border-b-2 border-primary-6000 -mb-px"
        : "text-text-color/70 border-b-2 border-transparent hover:text-text-color hover:border-primary-200"
    }`}
  >
    {label}
  </button>
);

const SubNavItem: FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`group flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
      active
        ? "bg-primary-6000 border border-primary-6000 text-on-primary"
        : "border border-transparent text-text-color/70 hover:border-primary-200 hover:text-primary-6000"
    }`}
  >
    <span
      className={`flex ${active ? "text-on-primary" : "text-text-color/60 group-hover:text-primary-6000"}`}
    >
      {icon}
    </span>
    <span>{label}</span>
  </button>
);

/* ---------- Odometer panel ---------- */

const OdometerPanel: FC<{
  phase: Phase;
  image: string | null;
  photoRecord: TripPhoto | null;
  loading: boolean;
  result: OdometerReadResult | null;
  error: string | null;
  onPick: (file: File) => void;
  preResult: OdometerReadResult | null;
  postResult: OdometerReadResult | null;
}> = ({
  phase,
  image,
  photoRecord,
  loading,
  result,
  error,
  onPick,
  preResult,
  postResult,
}) => {
  const inputId = `ti-odo-${phase}`;
  const distance =
    preResult && postResult
      ? Math.max(0, postResult.miles - preResult.miles)
      : null;
  const photoUrl = photoRecord?.image || image || null;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Odometer reading"
        subtitle={
          phase === "pre"
            ? "Capture the starting mileage before handing over keys. Mileage is auto-detected from the photo."
            : "Capture the ending mileage on vehicle return. Mileage is auto-detected from the photo."
        }
      />

      <Field label="Mileage">
        <div className="relative">
          <input
            type="text"
            readOnly
            value={
              !photoUrl
                ? "0"
                : result
                  ? result.miles.toLocaleString()
                  : ""
            }
            placeholder="0"
            className="w-full px-3.5 py-2.5 text-lg font-semibold text-text-color bg-secondary-50 border border-secondary-200 rounded-lg outline-none cursor-default font-mono"
          />
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-text-color/60 font-medium">
            mi
          </span>
        </div>
        <span className="mt-1 flex items-center gap-1.5 text-xs text-secondary-text-color">
          <SparklesIcon className="h-3.5 w-3.5 text-primary-200" />
          AI-read from the odometer photo. AI can make mistakes — please
          verify the reading.
        </span>
      </Field>

      <label
        htmlFor={inputId}
        className={`cursor-pointer rounded-xl border-[1.5px] border-dashed p-8 flex flex-col items-center justify-center gap-2.5 text-center transition-all border-secondary-200 bg-card-color hover:border-primary-200 ${
          loading ? "pointer-events-none opacity-70" : ""
        }`}
      >
        <span className="flex items-center justify-center w-11 h-11 rounded-full bg-card-color border border-secondary-100 text-text-color/70">
          <CameraIcon className="w-5 h-5" />
        </span>
        <div className="text-sm font-semibold text-text-color">
          {loading
            ? "Reading odometer…"
            : photoUrl
              ? "Retake odometer photo"
              : "Tap to capture odometer"}
        </div>
        <div className="text-xs text-secondary-text-color">PNG, JPG, WEBP</div>
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.currentTarget.value = "";
        }}
      />

      {photoUrl && (
        <div className="flex flex-col gap-3">
          <div className="text-sm font-semibold text-text-color">
            Captured odometer photo
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-secondary-200 bg-black">
                <img
                  src={photoUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-white/95 border border-secondary-200 rounded-full text-[11px] font-semibold text-green-700">
                  <CheckIcon className="w-3 h-3" />
                  <span>Captured</span>
                </div>
                {loading && (
                  <div className="absolute inset-0 bg-black/45 text-white flex items-center justify-center text-sm font-semibold">
                    Reading odometer…
                  </div>
                )}
              </div>
              <PhotoMeta
                createdAt={photoRecord?.created_at}
                addedBy={photoRecord?.added_by}
              />
            </div>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
          <ExclamationTriangleIcon className="w-4 h-4" />
          {error}
        </div>
      )}

      {distance !== null && (
        <div className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-semibold">
          <CheckIcon className="w-4 h-4" />
          Distance traveled: {distance.toLocaleString()} mi
        </div>
      )}
    </div>
  );
};

/* ---------- Car images panel ---------- */

const CarImagesPanel: FC<{
  phase: Phase;
  rootProps: any;
  inputProps: any;
  isDragActive: boolean;
  pendingItems: { file: File; url: string }[];
  onRemovePending: (idx: number) => void;
  onClearPending: () => void;
  onUpload: () => void;
  uploading: boolean;
  photos: TripPhoto[];
  onRemoveUploaded: (id: number) => void;
  deletingId: number | null;
  onPreview: (src: string) => void;
}> = ({
  phase,
  rootProps,
  inputProps,
  isDragActive,
  pendingItems,
  onRemovePending,
  onClearPending,
  onUpload,
  uploading,
  photos,
  onRemoveUploaded,
  deletingId,
  onPreview,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Vehicle photos & videos"
        subtitle={
          phase === "pre"
            ? "Document condition before start the trip."
            : "Document condition on vehicle return."
        }
      />

      <div
        {...rootProps}
        className={`cursor-pointer rounded-xl border-[1.5px] border-dashed p-8 flex flex-col items-center justify-center gap-2.5 text-center transition-all ${
          isDragActive
            ? "border-primary-200 bg-primary-50"
            : "border-secondary-200 bg-bg-color"
        }`}
      >
        <input {...inputProps} />
        <span
          className={`flex items-center justify-center w-11 h-11 rounded-full bg-bg-color border border-secondary-100 ${
            isDragActive ? "text-primary-200" : "text-text-color/70"
          }`}
        >
          <ArrowUpTrayIcon className="w-5 h-5" />
        </span>
        <div className="text-sm font-semibold text-text-color">
          {isDragActive ? "Drop files to upload" : "Drop or select files"}
        </div>
        <div className="text-xs text-text-color/60">
          PNG, JPG, WEBP, MP4, MOV, WEBM
        </div>
      </div>

      {pendingItems.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pendingItems.map(({ file: f, url }, i) => {
              const isVideo = f.type.startsWith("video");
              return (
                <div
                  key={url}
                  className="relative aspect-[4/3] rounded-xl overflow-hidden border border-secondary-200 bg-black group"
                >
                  {isVideo ? (
                    <video
                      src={`${url}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <button
                    onClick={() => onRemovePending(i)}
                    className="absolute top-2 right-2 w-[30px] h-[30px] rounded-lg bg-white/95 border border-secondary-200 flex items-center justify-center text-red-600"
                    title="Remove"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClearPending}
              disabled={uploading}
              className="px-4 py-2.5 text-sm font-semibold text-text-color bg-bg-color border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
            >
              Clear {pendingItems.length}
            </button>
            <button
              onClick={onUpload}
              disabled={uploading}
              className="px-4 py-2.5 text-sm font-semibold text-on-primary bg-primary-200 rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {uploading
                ? "Uploading…"
                : `Upload ${pendingItems.length} file${pendingItems.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="text-sm font-semibold text-text-color">
            Uploaded files ({photos.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => {
              const src = p.image;
              const ext = src?.split("?")[0].split(".").pop()?.toLowerCase();
              const isVideo = ["mp4", "mov", "webm"].includes(ext || "");
              return (
                <div key={p.id} className="flex flex-col gap-1.5">
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-secondary-200 bg-black">
                    {isVideo ? (
                      <video
                        src={`${src}#t=0.1`}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPreview(src)}
                        className="block h-full w-full cursor-zoom-in"
                        aria-label="Preview image"
                      >
                        <img
                          src={src}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )}
                    <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-white/95 border border-secondary-200 rounded-full text-[11px] font-semibold text-green-700">
                      <CheckIcon className="w-3 h-3" />
                      <span>Captured</span>
                    </div>
                  </div>
                  <PhotoMeta createdAt={p.created_at} addedBy={p.added_by} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {photos.length === 0 && pendingItems.length === 0 && (
        <div className="text-sm text-text-color/60 text-center py-2">
          No {phase === "pre" ? "pre-trip" : "post-trip"} files uploaded yet.
        </div>
      )}
    </div>
  );
};

/* ---------- Claim evidence panel ---------- */

const ClaimEvidencePanel: FC<{
  rootProps: any;
  inputProps: any;
  isDragActive: boolean;
  pendingItems: { file: File; url: string }[];
  onRemovePending: (idx: number) => void;
  onClearPending: () => void;
  onUpload: () => void;
  uploading: boolean;
  photos: TripPhoto[];
  onPreview: (src: string) => void;
}> = ({
  rootProps,
  inputProps,
  isDragActive,
  pendingItems,
  onRemovePending,
  onClearPending,
  onUpload,
  uploading,
  photos,
  onPreview,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Damage proof"
        subtitle="Upload photos or videos that document damage or issues observed during the rental."
      />

      <div
        {...rootProps}
        className={`cursor-pointer rounded-xl border-[1.5px] border-dashed p-8 flex flex-col items-center justify-center gap-2.5 text-center transition-all ${
          isDragActive
            ? "border-primary-200 bg-primary-50"
            : "border-secondary-200 bg-bg-color"
        } ${uploading ? "pointer-events-none opacity-70" : ""}`}
      >
        <input {...inputProps} />
        <span
          className={`flex items-center justify-center w-11 h-11 rounded-full bg-bg-color border border-secondary-100 ${
            isDragActive ? "text-primary-200" : "text-text-color/70"
          }`}
        >
          <ArrowUpTrayIcon className="w-5 h-5" />
        </span>
        <div className="text-sm font-semibold text-text-color">
          {isDragActive ? "Drop files to upload" : "Drop or select files"}
        </div>
        <div className="text-xs text-text-color/60">
          PNG, JPG, WEBP, MP4, MOV, WEBM
        </div>
      </div>

      {pendingItems.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pendingItems.map(({ file: f, url }, i) => {
              const isVideo = f.type.startsWith("video");
              return (
                <div
                  key={url}
                  className="relative aspect-[4/3] rounded-xl overflow-hidden border border-secondary-200 bg-black group"
                >
                  {isVideo ? (
                    <video
                      src={`${url}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <button
                    onClick={() => onRemovePending(i)}
                    disabled={uploading}
                    className="absolute top-2 right-2 w-[30px] h-[30px] rounded-lg bg-white/95 border border-secondary-200 flex items-center justify-center text-red-600 disabled:opacity-50"
                    title="Remove"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClearPending}
              disabled={uploading}
              className="px-4 py-2.5 text-sm font-semibold text-text-color bg-bg-color border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
            >
              Clear {pendingItems.length}
            </button>
            <button
              onClick={onUpload}
              disabled={uploading}
              className="px-4 py-2.5 text-sm font-semibold text-on-primary bg-primary-200 rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {uploading
                ? "Uploading…"
                : `Upload ${pendingItems.length} file${pendingItems.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="text-sm font-semibold text-text-color">
            Uploaded damage proof ({photos.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => {
              const src = p.image;
              const ext = src?.split("?")[0].split(".").pop()?.toLowerCase();
              const isVideo = ["mp4", "mov", "webm"].includes(ext || "");
              return (
                <div key={p.id} className="flex flex-col gap-1.5">
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-secondary-200 bg-black">
                    {isVideo ? (
                      <video
                        src={src}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPreview(src)}
                        className="block h-full w-full cursor-zoom-in"
                        aria-label="Preview image"
                      >
                        <img
                          src={src}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )}
                    <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-white/95 border border-secondary-200 rounded-full text-[11px] font-semibold text-red-700">
                      <ShieldExclamationIcon className="w-3 h-3" />
                      <span>{isVideo ? "Damage · Video" : "Damage"}</span>
                    </div>
                  </div>
                  <PhotoMeta createdAt={p.created_at} addedBy={p.added_by} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {photos.length === 0 && pendingItems.length === 0 && !uploading && (
        <div className="text-sm text-text-color/60 text-center py-2">
          No damage proof uploaded yet.
        </div>
      )}
    </div>
  );
};

/* ---------- Shared bits ---------- */

const SectionHeader: FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div className="flex flex-col gap-1">
    <div className="text-lg font-bold text-text-color">{title}</div>
    {subtitle && <div className="text-sm text-text-color/70">{subtitle}</div>}
  </div>
);

const Field: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-semibold text-text-color">{label}</span>
    {children}
  </label>
);

const PhotoMeta: FC<{
  createdAt?: string;
  addedBy?: TripPhoto["added_by"];
}> = ({ createdAt, addedBy }) => {
  const fullName = [addedBy?.first_name, addedBy?.last_name]
    .filter(Boolean)
    .join(" ");
  const uploader = fullName || "Unknown";
  let when = "";
  if (createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  }
  return (
    <div className="flex flex-col gap-0.5 px-0.5 text-[11px] leading-tight text-text-color/70">
      {when && <span className="font-semibold text-text-color">{when}</span>}
      <span>by {uploader}</span>
    </div>
  );
};
